import React, { useEffect } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import parse from "html-react-parser";
import UnstyledImage from "./image";
import { Link } from "gatsby";
import { faArrowRightLong } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

const VideoWrapper = styled.div`
  position: relative;
  margin: 8px;
  width: 100%;
  font-family: ${({ theme }) => theme.fontParagraphs.fontFamily};
  z-index: 1;
  &:first-child {
    margin-left: 0;
  }
  @media (max-width: ${({ theme }) => theme.breakpoints.phone}) {
    width: 100%;
    margin: 1rem 0;
  }
`;
const Image = styled(UnstyledImage)`
  width: 100%;
  height: 100%;
  object-fit: cover;
  position: absolute;
`;
const Video = styled.div`
  height: 500px;
  width: 100%;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  @media (max-width: ${({ theme }) => theme.breakpoints.phone}) {
    height: 220px;
  }
`;
const IconDiv = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  cursor: pointer;
`;
const IFrame = styled.iframe`
  width: 100%;
  height: 100%;
`;
const VideoFrame = styled.video`
  width: 100%;
  height: 100%;
`;
const TextDiv = styled.div`
  margin: 0;
  max-height: 200px;
`;
const Title = styled.h2`
  color: ${({ theme }) => theme.colors.primary};
  font-weight: 500;
  margin: 0.5rem 0 0 0;
`;
const Description = styled.p`
  margin: 10px 0;
  line-height: 1.5;
`;
const StyledLink = styled((props) => <Link {...props} />)`
  text-decoration: none;
  font-family: ${({ theme }) => theme.fontLinks.fontFamily};
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
`;
const Arrow = styled(FontAwesomeIcon)`
  color: ${({ theme }) => theme.colors.accent1};
`;
export default function VideoCard({
  imgUrl,
  title,
  description,
  siteUrl,
  urlLabel,
  youTubeID,
  vimeoID,
  imgAlt,
  image,
  videoUrl,
}) {
  const [isVisible, setVisible] = React.useState(true);
  const [play, setPlay] = React.useState(false);
  const [autoplay, setAutoplay] = React.useState(false);
  const playVid = () => {
    if (!image || youTubeID || vimeoID || videoUrl) {
      setVisible(false);
      setPlay(true);
      setAutoplay(true);
    }
  };

  const playVidImage = () => {
    if (!image) {
      setVisible(false);
      setAutoplay(false);
      setPlay(true);
    }
  };

  useEffect(() => {
    playVidImage();
  }, []);

  return (
    <VideoWrapper>
      <Video>
        {youTubeID && play && (
          <IFrame
            src={`https://www.youtube.com/embed/${youTubeID}?autoplay=${autoplay}`}
            frameBorder="0"
            allow="autoplay; fullscreen"
            allowFullScreen
          ></IFrame>
        )}
        {vimeoID && play && (
          <IFrame
            src={`https://player.vimeo.com/video/${vimeoID}?autoplay=${autoplay}`}
            frameBorder="0"
            width="100%"
            allow="autoplay; fullscreen"
            allowFullScreen
          ></IFrame>
        )}
        {videoUrl && play && (
          <VideoFrame controls autoPlay>
            <source src={videoUrl} type="video/mp4" />
          </VideoFrame>
        )}
        {isVisible && (
          <>
            <Image alt={imgAlt} src={imgUrl} image={image} />
            <IconDiv onClick={() => playVid()}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="58"
                height="68"
                viewBox="0 0 58 68"
              >
                <path
                  id="Polygon_21"
                  data-name="Polygon 21"
                  d="M34,0,68,58H0Z"
                  transform="translate(58) rotate(90)"
                  fill="#fff"
                />
              </svg>
            </IconDiv>
          </>
        )}
      </Video>
      <TextDiv>
        <Title>{title}</Title>
        <Description>{parse(description)}</Description>
        {siteUrl && (
          <StyledLink to={siteUrl}>
            {urlLabel}
            <span style={{ paddingLeft: "8px" }}>
              <Arrow icon={faArrowRightLong} size="1x" />
            </span>
          </StyledLink>
        )}
      </TextDiv>
    </VideoWrapper>
  );
}

VideoCard.propTypes = {
  imgUrl: PropTypes.object,
  title: PropTypes.string,
  description: PropTypes.string,
  siteUrl: PropTypes.string,
  urlLabel: PropTypes.string,
  youTubeID: PropTypes.string,
  vimeoID: PropTypes.string,
  imgAlt: PropTypes.string,
  image: PropTypes.object,
  videoUrl: PropTypes.string,
  videoSource: PropTypes.string,
};
VideoCard.defaultProps = {
  imgUrl: undefined,
  title: undefined,
  description: undefined,
  siteUrl: undefined,
  urlLabel: "Les mer",
  youTubeID: undefined,
  vimeoID: undefined,
  imgAlt: undefined,
  image: undefined,
  videoUrl: undefined,
};
